package com.havn.app.features.followups.data

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.supabase.havnSlowSqlDetail
import com.havn.app.features.followups.domain.FollowUp
import com.havn.app.features.followups.domain.FollowUpChannel
import com.havn.app.features.followups.domain.FollowUpDraft
import com.havn.app.features.followups.domain.FollowUpRepository
import com.havn.app.features.followups.domain.FollowUpStatus
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

class SupabaseFollowUpRepository(
    private val client: SupabaseClient
) : FollowUpRepository {
    override suspend fun followUpsForAgent(agentId: String): Result<List<FollowUp>> {
        return runCatching {
            havnSlowSqlDetail("followUps.recent(agent_id=$agentId, offset=0, limit=$FOLLOW_UP_PAGE_SIZE)") {
                client.postgrest.rpc(
                    function = RECENT_FOLLOW_UPS_RPC,
                    parameters = RecentFollowUpsParams(
                        limit = FOLLOW_UP_PAGE_SIZE,
                        offset = 0
                    )
                )
                    .decodeList<FollowUpRow>()
            }
                .map { it.toDomain() }
        }.onFailure {
            HavnLogger.error("FollowUps", "Failed to load follow-ups for $agentId", it)
        }
    }

    override suspend fun saveDraft(agentId: String, draft: FollowUpDraft): Result<FollowUp> {
        return runCatching {
            HavnLogger.info("FollowUps", "Saving ${draft.channel.databaseValue} follow-up draft")
            havnSlowSqlDetail("followUps.saveDraft(agent_id=$agentId, channel=${draft.channel.databaseValue})") {
                client.from(FOLLOW_UPS)
                    .insert(draft.toInsertRow(agentId)) {
                        select()
                    }
                    .decodeSingle<FollowUpRow>()
            }
                .toDomain()
        }.onFailure {
            HavnLogger.error("FollowUps", "Failed to save follow-up draft", it)
        }
    }

    override suspend fun markSent(
        agentId: String,
        followUpId: String,
        completedAt: String
    ): Result<FollowUp> {
        return runCatching {
            HavnLogger.info("FollowUps", "Marking follow-up $followUpId as sent")
            havnSlowSqlDetail("followUps.markSent(agent_id=$agentId, follow_up_id=$followUpId)") {
                client.from(FOLLOW_UPS)
                    .update(FollowUpSentUpdateRow(FollowUpStatus.Completed.databaseValue, completedAt)) {
                        filter {
                            eq("id", followUpId)
                            eq("assigned_agent_id", agentId)
                        }
                        select()
                    }
                    .decodeSingle<FollowUpRow>()
            }
                .toDomain()
        }.onFailure {
            HavnLogger.error("FollowUps", "Failed to mark follow-up $followUpId as sent", it)
        }
    }

    private companion object {
        const val FOLLOW_UP_PAGE_SIZE = 50
        const val RECENT_FOLLOW_UPS_RPC = "recent_follow_ups_for_current_user"
        const val FOLLOW_UPS = "follow_ups"
    }
}

@Serializable
private data class RecentFollowUpsParams(
    @SerialName("p_limit") val limit: Int,
    @SerialName("p_offset") val offset: Int
)

@Serializable
private data class FollowUpRow(
    val id: String,
    @SerialName("visitor_id") val visitorId: String,
    @SerialName("assigned_agent_id") val assignedAgentId: String,
    val channel: String,
    val status: String,
    @SerialName("due_at") val dueAt: String,
    @SerialName("completed_at") val completedAt: String? = null,
    val subject: String,
    val body: String? = null,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("updated_at") val updatedAt: String? = null
) {
    fun toDomain(): FollowUp = FollowUp(
        id = id,
        visitorId = visitorId,
        assignedAgentId = assignedAgentId,
        channel = FollowUpChannel.fromDatabase(channel),
        status = FollowUpStatus.fromDatabase(status),
        dueAt = dueAt,
        completedAt = completedAt,
        subject = subject,
        body = body,
        createdAt = createdAt,
        updatedAt = updatedAt
    )
}

@Serializable
private data class FollowUpInsertRow(
    @SerialName("visitor_id") val visitorId: String,
    @SerialName("assigned_agent_id") val assignedAgentId: String,
    @SerialName("owner_agent_id") val ownerAgentId: String,
    val channel: String,
    val status: String,
    @SerialName("due_at") val dueAt: String,
    @SerialName("completed_at") val completedAt: String? = null,
    val subject: String,
    val body: String
)

@Serializable
private data class FollowUpSentUpdateRow(
    val status: String,
    @SerialName("completed_at") val completedAt: String
)

private fun FollowUpDraft.toInsertRow(agentId: String): FollowUpInsertRow = FollowUpInsertRow(
    visitorId = visitorId,
    assignedAgentId = agentId,
    ownerAgentId = agentId,
    channel = channel.databaseValue,
    status = status.databaseValue,
    dueAt = dueAt,
    completedAt = completedAt,
    subject = subject.trim().ifBlank { "${channel.label} follow-up" },
    body = body.trim()
)
